/**
 * Self-serve signup constants + helpers (Phase 4). Pure, shared by API + frontend.
 * Design: docs/billing/signup-and-trials.md.
 */

/**
 * Whether self-serve signups must add a card before the app unlocks.
 *   no_card      — trial app immediately; goes read-only at trialEndsAt (block_on_expiry)
 *   card_required— Stripe Checkout (card + 7d trial) before the app unlocks; auto-charges day 8
 * Snapshotted onto the tenant at signup (signupCardMode) so flipping this global
 * never breaks in-flight trials. DECIDED 2026-06-11: launch with no_card.
 */
export const SIGNUP_CARD_MODE: 'no_card' | 'card_required' = 'no_card';

/** Self-serve trial length for the time-bound tiers (basic/pro/elite). */
export const SELF_SERVE_TRIAL_DAYS = 7;

/** Agency self-signup gets a credit-bound trial instead of a time-bound one. */
export const AGENCY_TRIAL_CREDITS = 500;

/** Current Terms of Service version (YYYY-MM). Bump on material ToS changes —
 *  triggers re-acceptance (forward-compat; see pricing plan §17.5). */
export const CURRENT_TOS_VERSION = '2026-06';

/** Public CGV (terms of sale) page — linked from Stripe Checkout consent. */
export const TOS_URL = 'https://traaker.ai/cgv.html';

/** Tiers a user can pick on the public /signup page. Enterprise is admin-only. */
export const SELF_SIGNUP_TIERS = ['basic', 'pro', 'elite', 'agency'] as const;
export type SelfSignupTier = (typeof SELF_SIGNUP_TIERS)[number];

/**
 * Self-serve plan DISPLAY metadata (basic/pro/elite) — the single source of truth
 * for in-app price presentation (the plan-selection popup). Prices mirror
 * the Stripe catalog (scripts/setup-stripe-catalog.ts) + pricing-and-billing.md §3.1:
 * monthly cents, and yearly = monthly × 10 (2 months free). Values must stay in
 * sync with TIER_LIMITS in quota.ts.
 *
 * Feature COPY is NOT hardcoded here anymore (that caused real bugs: French text
 * leaking into the English UI, inconsistent row ordering per tier, and stale
 * "adds over Basic" framing that omitted Earned Media/Content Creation on Basic
 * even though both exist on every tier, just at a smaller quota). Feature rows
 * are now derived at render time from `PLAN_FEATURE_ROWS` + `METRIC_REGISTRY`
 * (`resolveEntitlement`) — see `planFeatureLabel` below.
 */
export interface SelfServePlanDisplay {
  tier: 'basic' | 'pro' | 'elite';
  label: string;
  monthlyCents: number;
  yearlyCents: number;
  recommended?: boolean;
}

export const SELF_SERVE_PLANS: SelfServePlanDisplay[] = [
  { tier: 'basic', label: 'Basic', monthlyCents: 8900, yearlyCents: 89000 },
  { tier: 'pro', label: 'Pro', monthlyCents: 21900, yearlyCents: 219000, recommended: true },
  { tier: 'elite', label: 'Elite', monthlyCents: 42900, yearlyCents: 429000 },
];

/** Ordered self-serve tier rank (basic < pro < elite) for upgrade/downgrade direction. */
export const SELF_SERVE_TIER_RANK: Record<string, number> = { basic: 0, pro: 1, elite: 2 };

export function selfServePlan(tier: string): SelfServePlanDisplay | undefined {
  return SELF_SERVE_PLANS.find((p) => p.tier === tier);
}

/**
 * True when the tier buys from the self-serve catalog (basic/pro/elite) — i.e.
 * the ONLY tiers for which a plan-upgrade CTA is meaningful. Agency and
 * enterprise are contract/credit tiers: agency buys credits + agency plans,
 * enterprise is negotiated by the sales team. Showing them a "pass to Pro" CTA
 * offers a downgrade dressed as an upgrade, and `/billing/checkout/self-serve`
 * rejects them anyway.
 *
 * Use this (not an inline `tier !== 'agency'`) at every upgrade-CTA and
 * quota-badge site so the gate stays in one place.
 */
export function isSelfServeTier(tier?: string): boolean {
  return !!tier && tier in SELF_SERVE_TIER_RANK;
}

/**
 * Canonical, tier-agnostic ORDER of feature rows shown on every plan card and
 * in the admin "offer" email — the fix for cards listing the same features in
 * a different order per tier. `kind` picks the rendering:
 *   - 'metric'  → a numeric METRIC_REGISTRY entry, rendered "{{count}} <label>"
 *   - 'engines' → the AI-engine allowlist — rendered with logos on the frontend
 *   - 'boolean' → an on/off registry entry — rendered only when true
 */
export type PlanFeatureRowKind = 'metric' | 'engines' | 'boolean';
export interface PlanFeatureRowDef {
  metricKey: string;
  kind: PlanFeatureRowKind;
}
export const PLAN_FEATURE_ROWS: PlanFeatureRowDef[] = [
  { metricKey: 'brands', kind: 'metric' },
  { metricKey: 'zones', kind: 'metric' },
  { metricKey: 'languages', kind: 'metric' },
  { metricKey: 'engines', kind: 'engines' },
  { metricKey: 'prompts', kind: 'metric' },
  { metricKey: 'competitors', kind: 'metric' },
  { metricKey: 'pages_per_website_audit', kind: 'metric' },
  { metricKey: 'fetch_per_earned_media', kind: 'metric' },
  { metricKey: 'content_creations', kind: 'metric' },
  { metricKey: 'tasks', kind: 'metric' },
  { metricKey: 'users', kind: 'metric' },
  { metricKey: 'api_access', kind: 'boolean' },
];

/**
 * Bilingual sentence for one metric row at a given count. Kept as plain TS
 * (not an i18n JSON fragment) so the SAME function drives both the frontend
 * plan popup (fr/en) and the backend "offer" welcome email (fr-only) — a
 * single source of truth for the numbers AND the wording, so they can never
 * drift the way the old hardcoded per-tier string arrays did.
 */
export function planFeatureLabel(metricKey: string, count: number, lang: 'fr' | 'en' = 'fr'): string {
  const s = (n: number) => (n === 1 ? '' : 's');
  if (lang === 'en') {
    switch (metricKey) {
      case 'brands': return `${count} brand${s(count)}`;
      case 'zones': return `${count} zone${s(count)}`;
      case 'languages': return `${count} language${count === 1 ? '' : 's'}`;
      case 'prompts': return `${count} prompts`;
      case 'competitors': return `${count} tracked competitor${s(count)}`;
      case 'pages_per_website_audit': return `${count} pages / website audit`;
      case 'fetch_per_earned_media': return `${count} third-party pages / Earned Media audit`;
      case 'content_creations': return `${count} content creation${s(count)} / month`;
      case 'tasks': return `${count} task${s(count)}`;
      case 'users': return `${count} user${s(count)}`;
      case 'api_access': return 'API / MCP access';
      default: return '';
    }
  }
  switch (metricKey) {
    case 'brands': return `${count} marque${s(count)}`;
    case 'zones': return `${count} zone${s(count)}`;
    case 'languages': return `${count} langue${s(count)}`;
    case 'prompts': return `${count} prompts`;
    case 'competitors': return `${count} concurrent${s(count)} suivi${s(count)}`;
    case 'pages_per_website_audit': return `${count} pages / audit site`;
    case 'fetch_per_earned_media': return `${count} pages tierces / audit Earned Media`;
    case 'content_creations': return `${count} création${s(count)} de contenu / mois`;
    case 'tasks': return `${count} tâche${s(count)}`;
    case 'users': return `${count} utilisateur${s(count)}`;
    case 'api_access': return 'Accès API / MCP';
    default: return '';
  }
}

/** " (au lieu de {{basis}})" / " (instead of {{basis}})" — appended to a delta row. */
export function planFeatureDeltaSuffix(basisCount: number, lang: 'fr' | 'en' = 'fr'): string {
  return lang === 'en' ? ` (instead of ${basisCount})` : ` (au lieu de ${basisCount})`;
}

export function isSelfSignupTier(t: string): t is SelfSignupTier {
  return (SELF_SIGNUP_TIERS as readonly string[]).includes(t);
}

/**
 * Free / personal email domains rejected at provision time — this is the B2B
 * gate (Google can't be Workspace-restricted at the IdP, so the filter lives
 * here and runs for every auth method). Not exhaustive; covers the high-volume
 * consumer providers. Extend as needed.
 */
export const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'outlook.com', 'outlook.fr', 'hotmail.com', 'hotmail.fr', 'live.com', 'live.fr', 'msn.com',
  'yahoo.com', 'yahoo.fr', 'ymail.com', 'rocketmail.com',
  'icloud.com', 'me.com', 'mac.com',
  'proton.me', 'protonmail.com', 'pm.me',
  'aol.com', 'gmx.com', 'gmx.fr', 'gmx.net', 'mail.com', 'zoho.com', 'yandex.com',
  'orange.fr', 'wanadoo.fr', 'free.fr', 'sfr.fr', 'laposte.net', 'bbox.fr', 'numericable.fr',
]);

/** Disposable / throwaway domains — also rejected. Small high-signal set. */
export const DISPOSABLE_EMAIL_DOMAINS = new Set([
  'mailinator.com', 'guerrillamail.com', 'guerrillamail.info', '10minutemail.com',
  'tempmail.com', 'temp-mail.org', 'throwaway.email', 'yopmail.com', 'getnada.com',
  'trashmail.com', 'sharklasers.com', 'maildrop.cc', 'dispostable.com', 'fakeinbox.com',
]);

/** Days after trialEndsAt that a non-converted, non-onboarded tenant is purged. */
export const TRIAL_REAPER_GRACE_DAYS = 15;

/**
 * Constant partition key for the sparse `trial-index` GSI on the tenants table.
 * Written to `tenant.trialIndexPK` ONLY while a block_on_expiry trial is live, so
 * the daily trial-lifecycle job range-queries expired trials by `trialEndsAt`
 * instead of scanning. Cleared (REMOVE) on conversion to active.
 */
export const TRIAL_INDEX_PK = 'TRIAL';

/**
 * Constant partition key for the sparse `purge-index` GSI on the tenants table.
 * Written to `tenant.purgeIndexPK` alongside `purgeDueAt` when a subscription
 * terminally ends, so the daily reaper range-queries due tenants by date instead
 * of scanning. Cleared (REMOVE) on reactivation.
 */
export const PURGE_INDEX_PK = 'PURGE';

/**
 * Days after a subscription terminally ends before the account is purged when the
 * customer explicitly opted into erasure (`settings.deleteDataOnCancel`). Matches
 * the "suppression dans les 30 jours" commitment in the privacy policy + DPA §9.
 */
export const CANCEL_PURGE_GRACE_DAYS = 30;

/**
 * Days a cancelled account is kept when the customer did NOT opt into erasure, so
 * they can resubscribe without starting over. Matches the "conservées 12 mois après
 * résiliation" commitment in the privacy policy + CGV art. 10.
 */
export const CANCEL_RETENTION_DAYS = 365;

/**
 * Is a tenant's time-bound trial expired and unconverted? True only for the
 * block_on_expiry path: trialMode='block_on_expiry', a trialEndsAt in the past,
 * and billing not active. Agency (no trialEndsAt) and card_required (Stripe
 * auto-charges) never hit this. Pure — used by the BillingGuard AND the frontend
 * paywall so they agree. `nowMs` injected for testability.
 */
export function isTrialExpired(
  tenant: {
    trialMode?: string;
    trialEndsAt?: string;
    billingStatus?: string;
  },
  nowMs: number,
): boolean {
  if (tenant.trialMode !== 'block_on_expiry') return false;
  if (!tenant.trialEndsAt) return false;
  if (tenant.billingStatus === 'active') return false;
  return new Date(tenant.trialEndsAt).getTime() < nowMs;
}

export type EmailGateResult =
  | { ok: true; domain: string }
  | { ok: false; reason: 'invalid' | 'free_email' | 'disposable'; domain?: string };

/**
 * B2B work-email gate. Rejects malformed addresses, free/personal providers,
 * and disposable domains. Pure — call at provision time before any write.
 */
export function checkBusinessEmail(email: string): EmailGateResult {
  const e = (email || '').trim().toLowerCase();
  const at = e.lastIndexOf('@');
  if (at <= 0 || at === e.length - 1 || !e.includes('.', at)) {
    return { ok: false, reason: 'invalid' };
  }
  const domain = e.slice(at + 1);
  if (FREE_EMAIL_DOMAINS.has(domain)) return { ok: false, reason: 'free_email', domain };
  if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) return { ok: false, reason: 'disposable', domain };
  return { ok: true, domain };
}
